import React, { useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  X,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";

export default function AttendanceRegularization() {
  const [requests, setRequests] = useState([
    {
      id: 1,
      date: "2025-01-24",
      inTime: "09:45",
      outTime: "18:15",
      reason: "Missed punch-in",
      status: "Approved",
      attachment: null,
    },
    {
      id: 2,
      date: "2025-01-26",
      inTime: "",
      outTime: "",
      reason: "Device not working",
      status: "Pending",
      attachment: null,
    },
  ]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = {
    date: "",
    inTime: "",
    outTime: "",
    reason: "",
    attachment: null,
  };

  const [form, setForm] = useState(emptyForm);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (req) => {
    setEditing(req);
    setForm(req);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  const saveRequest = (e) => {
    e.preventDefault();

    if (!form.date.trim()) return alert("Please select date");

    if (editing) {
      setRequests((prev) =>
        prev.map((r) => (r.id === editing.id ? { ...form, status: "Pending" } : r))
      );
    } else {
      setRequests((prev) => [
        {
          id: Date.now(),
          ...form,
          status: "Pending",
        },
        ...prev,
      ]);
    }

    closeDrawer();
  };

  const deleteRequest = (id) => {
    if (!window.confirm("Delete this request?")) return;
    setRequests((prev) => prev.filter((r) => r.id !== id));
  };

  const updateStatus = (id, status) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status } : r))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="text-teal-600" /> Attendance Regularization
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage in/out punch corrections and approval workflow.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 hover:bg-teal-700"
        >
          <Plus size={16} /> New Regularization
        </button>
      </div>

      {/* Requests Table */}
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b">
              <th className="pb-3">Date</th>
              <th className="pb-3">In Time</th>
              <th className="pb-3">Out Time</th>
              <th className="pb-3">Reason</th>
              <th className="pb-3">Status</th>
              <th className="pb-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-3">{r.date}</td>
                <td className="py-3">{r.inTime || "-"}</td>
                <td className="py-3">{r.outTime || "-"}</td>
                <td className="py-3">{r.reason}</td>

                {/* Status */}
                <td className="py-3">
                  {r.status === "Pending" && (
                    <span className="text-yellow-600 font-medium">Pending</span>
                  )}
                  {r.status === "Approved" && (
                    <span className="text-green-600 font-medium">Approved</span>
                  )}
                  {r.status === "Rejected" && (
                    <span className="text-red-600 font-medium">Rejected</span>
                  )}
                </td>

                {/* Actions */}
                <td className="py-3 flex gap-3">
                  <button
                    className="text-indigo-600 hover:text-indigo-900"
                    onClick={() => openEdit(r)}
                  >
                    <Edit size={16} />
                  </button>

                  <button
                    className="text-red-600 hover:text-red-900"
                    onClick={() => deleteRequest(r.id)}
                  >
                    <Trash2 size={16} />
                  </button>

                  {r.status === "Pending" && (
                    <>
                      <button
                        onClick={() => updateStatus(r.id, "Approved")}
                        className="text-green-600 hover:text-green-900"
                      >
                        <CheckCircle size={18} />
                      </button>

                      <button
                        onClick={() => updateStatus(r.id, "Rejected")}
                        className="text-red-600 hover:text-red-900"
                      >
                        <XCircle size={18} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {requests.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-500">
                  No regularization requests submitted.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer Modal */}
      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editing ? "Edit Regularization Request" : "New Regularization Request"}
      >
        <form onSubmit={saveRequest} className="space-y-6">
          {/* Date */}
          <div>
            <label className="text-sm text-gray-700">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="border rounded p-2 w-full mt-1"
              required
            />
          </div>

          {/* In Time & Out Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">In Time</label>
              <input
                type="time"
                value={form.inTime}
                onChange={(e) => setForm({ ...form, inTime: e.target.value })}
                className="border rounded p-2 w-full mt-1"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Out Time</label>
              <input
                type="time"
                value={form.outTime}
                onChange={(e) => setForm({ ...form, outTime: e.target.value })}
                className="border rounded p-2 w-full mt-1"
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm text-gray-700">Reason</label>
            <textarea
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="border rounded p-2 w-full mt-1"
              required
            />
          </div>

          {/* Attachments (mock) */}
          <div>
            <label className="text-sm text-gray-700">Attachment (optional)</label>
            <input
              type="file"
              onChange={(e) =>
                setForm({ ...form, attachment: e.target.files?.[0] || null })
              }
              className="mt-1"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeDrawer} className="px-4 py-2 bg-gray-200 rounded">
              Cancel
            </button>

            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded">
              {editing ? "Update Request" : "Create Request"}
            </button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}

/* Drawer Component */
function Drawer({ open, onClose, title, children }) {
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 bg-white w-full md:w-3/5 lg:w-2/5 xl:w-1/3 h-full z-50 shadow-2xl transform transition-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>

            <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 72px)" }}>
            {children}
          </div>
        </div>
      </aside>
    </>
  );
}
